(function () {
  'use strict';

  if (window.DrboVideoPlaylist) return;

  const STATE_EVENTS = [
    'pause',
    'play',
    'ratechange',
    'seeked',
    'seeking',
    'timeupdate',
    'volumechange'
  ];

  const isPendingPlaceholder = (value) => /^__(?:CDN|POSTER)_/.test(value || '');
  const isFiniteDuration = (value) => Number.isFinite(value) && value >= 0;

  function normalizeSource(source) {
    if (typeof source === 'string') return { url: source, duration: Number.NaN };
    if (!source || typeof source !== 'object') return null;

    const url = source.url || source.src;
    let duration = Number(source.duration);
    if (!isFiniteDuration(duration) && Number.isFinite(Number(source.durationMs))) {
      duration = Number(source.durationMs) / 1000;
    }

    return { url, duration: isFiniteDuration(duration) ? duration : Number.NaN };
  }

  function dispatch(video, name, detail) {
    if (typeof CustomEvent !== 'function') return;
    video.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  function create(video, options) {
    const settings = options || {};
    const entries = Array.isArray(settings.sources)
      ? settings.sources
        .map(normalizeSource)
        .filter((source) => source?.url && !isPendingPlaceholder(source.url))
      : [];

    if (!video || typeof video.addEventListener !== 'function' || entries.length === 0) return null;

    const durations = entries.map((source) => source.duration);
    const onStateChange = typeof settings.onStateChange === 'function' ? settings.onStateChange : null;
    const accessibleLabel = video.getAttribute('aria-label') || video.getAttribute('title') || (document.documentElement.lang.toLowerCase().split('-')[0] === 'en' ? 'Video by DRBO Greenenergy' : 'Video von DRBO Greenenergy');
    let currentPart = 0;
    let destroyed = false;
    let sourceLoaded = false;
    let shouldLoop = Boolean(settings.loop);
    let durationPromise = null;
    let loadSequence = 0;

    video.loop = false;
    video.playsInline = true;
    video.setAttribute('aria-label', accessibleLabel);

    function aggregateDuration() {
      return durations.every(isFiniteDuration)
        ? durations.reduce((total, duration) => total + duration, 0)
        : Number.NaN;
    }

    function elapsedBeforeCurrentPart() {
      const previousDurations = durations.slice(0, currentPart);
      return previousDurations.every(isFiniteDuration)
        ? previousDurations.reduce((total, duration) => total + duration, 0)
        : 0;
    }

    function getState() {
      const localTime = Number.isFinite(Number(video.currentTime)) ? Number(video.currentTime) : 0;
      return {
        currentTime: elapsedBeforeCurrentPart() + localTime,
        duration: aggregateDuration(),
        paused: Boolean(video.paused),
        muted: Boolean(video.muted || video.volume === 0),
        volume: Number(video.volume),
        playbackRate: Number(video.playbackRate),
        currentPart,
        partCount: entries.length
      };
    }

    function emitState() {
      if (destroyed) return;
      const state = getState();
      if (onStateChange) {
        try {
          onStateChange(state);
        } catch (error) {
          // A consumer callback must not interrupt playback.
        }
      }
      dispatch(video, 'drbo:video-playlist-statechange', state);
    }

    function updateCurrentDuration() {
      const duration = Number(video.duration);
      if (isFiniteDuration(duration)) durations[currentPart] = duration;
      emitState();
    }

    function probeDuration(entry) {
      if (isFiniteDuration(entry.duration)) return Promise.resolve(entry.duration);

      return new Promise((resolve) => {
        const probe = document.createElement('video');
        let settled = false;
        const timeout = window.setTimeout(() => finish(Number.NaN), 15000);

        function finish(duration) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          probe.removeEventListener('loadedmetadata', handleLoadedMetadata);
          probe.removeEventListener('error', handleError);
          probe.removeAttribute('src');
          probe.load();
          resolve(duration);
        }

        function handleLoadedMetadata() {
          finish(Number(probe.duration));
        }

        function handleError() {
          finish(Number.NaN);
        }

        probe.preload = 'metadata';
        probe.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
        probe.addEventListener('error', handleError, { once: true });
        probe.src = entry.url;
        probe.load();
      });
    }

    function ensureDurations() {
      if (durations.every(isFiniteDuration)) return Promise.resolve(durations);
      if (durationPromise) return durationPromise;

      durationPromise = Promise.all(entries.map((entry) => probeDuration(entry)))
        .then((resolvedDurations) => {
          resolvedDurations.forEach((duration, index) => {
            if (isFiniteDuration(duration)) durations[index] = duration;
          });
          emitState();
          return durations;
        });

      return durationPromise;
    }

    function loadPart(partIndex, { autoplay = false, time = 0 } = {}) {
      if (destroyed || !entries[partIndex]) return Promise.resolve();

      const sequence = ++loadSequence;
      const state = {
        muted: video.muted,
        volume: video.volume,
        playbackRate: video.playbackRate
      };

      currentPart = partIndex;
      sourceLoaded = true;
      video.setAttribute(
        'aria-label',
        entries.length > 1 ? `${accessibleLabel}, Teil ${currentPart + 1} von ${entries.length}` : accessibleLabel
      );
      video.src = entries[currentPart].url;
      video.load();
      video.muted = state.muted;
      video.volume = state.volume;
      video.playbackRate = state.playbackRate;

      if (time > 0) {
        video.addEventListener('loadedmetadata', () => {
          if (sequence !== loadSequence || destroyed) return;
          video.currentTime = Math.min(time, Math.max(Number(video.duration) - 0.05, 0));
          emitState();
        }, { once: true });
      }

      dispatch(video, 'drbo:video-playlist-partchange', {
        index: currentPart,
        count: entries.length,
        state: getState()
      });

      emitState();
      return autoplay ? video.play().catch(() => {}) : Promise.resolve();
    }

    function play() {
      if (destroyed) return Promise.resolve();
      if (!sourceLoaded) return loadPart(currentPart, { autoplay: true });
      return video.play();
    }

    function pause() {
      if (!destroyed) video.pause();
    }

    function applySeek(targetSeconds) {
      const duration = aggregateDuration();
      if (!isFiniteDuration(duration)) return;

      const target = Math.min(Math.max(Number(targetSeconds) || 0, 0), Math.max(duration - 0.05, 0));
      let elapsed = 0;
      let targetPart = entries.length - 1;

      for (let index = 0; index < durations.length; index += 1) {
        if (target < elapsed + durations[index] || index === durations.length - 1) {
          targetPart = index;
          break;
        }
        elapsed += durations[index];
      }

      const localTime = target - elapsed;
      const autoplay = sourceLoaded && !video.paused;
      if (!sourceLoaded || targetPart !== currentPart) {
        loadPart(targetPart, { autoplay, time: localTime });
      } else {
        video.currentTime = localTime;
        emitState();
      }
    }

    function seek(seconds) {
      if (destroyed) return Promise.resolve();
      return ensureDurations().then(() => applySeek(seconds));
    }

    function setMuted(muted) {
      if (!destroyed) video.muted = Boolean(muted);
    }

    function setLoop(loop) {
      shouldLoop = Boolean(loop);
    }

    function handleEnded() {
      if (currentPart < entries.length - 1) {
        loadPart(currentPart + 1, { autoplay: true });
        return;
      }

      dispatch(video, 'drbo:video-playlist-complete', getState());
      if (shouldLoop) loadPart(0, { autoplay: true });
      else loadPart(0);
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      STATE_EVENTS.forEach((eventName) => video.removeEventListener(eventName, emitState));
      video.removeEventListener('durationchange', updateCurrentDuration);
      video.removeEventListener('loadedmetadata', updateCurrentDuration);
      video.removeEventListener('ended', handleEnded);
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    STATE_EVENTS.forEach((eventName) => video.addEventListener(eventName, emitState));
    video.addEventListener('durationchange', updateCurrentDuration);
    video.addEventListener('loadedmetadata', updateCurrentDuration);
    video.addEventListener('ended', handleEnded);
    ensureDurations();
    emitState();

    return { play, pause, seek, setMuted, setLoop, getState, destroy };
  }

  function mount(options) {
    const settings = options || {};
    const container = settings.container;
    const config = settings.config || {};
    if (!container || !config.native) return null;

    const video = document.createElement('video');
    const title = (settings.title || (document.documentElement.lang.toLowerCase().split('-')[0] === 'en' ? 'Video by DRBO Greenenergy' : 'Video von DRBO Greenenergy')).trim();
    video.className = settings.className || '';
    video.controls = true;
    video.preload = 'metadata';
    video.setAttribute('aria-label', title);
    video.setAttribute('title', title);

    if (config.poster && !isPendingPlaceholder(config.poster)) video.poster = config.poster;

    const controller = create(video, {
      sources: config.sources,
      loop: settings.loop,
      onStateChange: settings.onStateChange
    });
    if (!controller) return null;

    container.appendChild(video);
    controller.play();

    return {
      ...controller,
      element: video,
      destroy() {
        controller.destroy();
        video.remove();
      }
    };
  }

  function parseManagedSources(value) {
    if (!value) return [];
    try {
      const sources = JSON.parse(value);
      return Array.isArray(sources) ? sources.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  function initManagedVideos(scope) {
    const videos = [];
    if (scope instanceof HTMLVideoElement && scope.matches('[data-drbo-video-playlist]')) videos.push(scope);
    scope?.querySelectorAll?.('video[data-drbo-video-playlist]').forEach((video) => videos.push(video));

    videos.forEach((video) => {
      if (video.dataset.drboPlaylistReady === 'true') return;
      const controller = create(video, { sources: parseManagedSources(video.dataset.videoSources) });
      if (!controller) return;
      video.dataset.drboPlaylistReady = 'true';
      video.drboPlaylistController = controller;
    });
  }

  window.DrboVideoPlaylist = { create, mount, initManagedVideos };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initManagedVideos(document), { once: true });
  } else {
    initManagedVideos(document);
  }
  document.addEventListener('theme:all:loaded', () => initManagedVideos(document));
  document.addEventListener('shopify:section:load', (event) => initManagedVideos(event.target));
})();
